import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { MyFranchiseDTO, ProductDTO } from '@bifeai/shared';
import { Badge, Button, Callout, Card, CardBody, EmptyState, PageHeader, Select, Skeleton, Spinner, useToast } from '@bifeai/ui';
import { Check, History, Percent, Tags, Undo2 } from 'lucide-react';
import { useDeferredValue, useEffect, useState } from 'react';
import { CategoryGlyph, SearchBox, Toolbar } from '../components/inventory';
import { api, errorMessage } from '../lib/api';
import { invalidateInventory, useCatalogAccess, useCategories, useProducts } from '../lib/catalog';
import { formatDate, formatMoney } from '../lib/format';
import { useSession } from '../lib/session';
import { centsToPesosInput, pesosToCents, priceUnit } from '../lib/units';
import { BulkPriceDialog } from './products/BulkPriceDialog';
import { PriceHistoryDialog } from './products/PriceDialog';

/** Lista de precios editable: se escribe el precio y se guarda con Enter o con el tilde. */
/** Franquicia: qué le permite la marca con los precios. */
function FranchisePriceNote() {
  const me = useQuery({ queryKey: ['franchise', 'me'], queryFn: () => api.get<MyFranchiseDTO>('/franchise') });
  const s = me.data?.settings;
  if (!s) return null;
  return (
    <Callout tone="info">
      {s.priceMode === 'BRAND'
        ? 'Tu franquicia usa los precios de la marca: los ves acá, pero los cambia la marca.'
        : s.priceMode === 'RANGE'
          ? `Podés poner tus precios hasta ${s.priceRangePct} % arriba o abajo del precio de la marca. Solo cambian en tu local.`
          : 'Podés poner tus propios precios. Solo cambian en tu local; los de la marca no se tocan.'}
    </Callout>
  );
}

export function PricesPage() {
  const { isFranchiseUser } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const access = useCatalogAccess();
  const [search, setSearch] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [bulk, setBulk] = useState(false);
  const [history, setHistory] = useState<ProductDTO | null>(null);
  const deferred = useDeferredValue(search.trim());
  const categories = useCategories();
  const products = useProducts({
    search: deferred || undefined,
    categoryId: categoryId || undefined,
  });

  const showBranch = Boolean(access.priceBranchId);
  const editGeneral = access.canGeneralPrice;
  const editBranch = access.canPrices && showBranch;

  async function save(p: ProductDTO, branchId: string | null, cents: number) {
    try {
      await api.put(`/products/${p.id}/price`, { priceCents: cents, branchId });
      toast.success('Precio guardado', p.name);
      await invalidateInventory(qc);
    } catch (err) {
      toast.error('No se pudo guardar', errorMessage(err));
      throw err;
    }
  }

  async function reset(p: ProductDTO) {
    if (!access.priceBranchId) return;
    try {
      await api.del(`/products/${p.id}/price/${access.priceBranchId}`);
      toast.success('Vuelve al precio general', p.name);
      await invalidateInventory(qc);
    } catch (err) {
      toast.error('No se pudo cambiar', errorMessage(err));
    }
  }

  return (
    <>
      <PageHeader
        title="Precios"
        subtitle={
          showBranch
            ? `Precio general de la cadena y precio que se cobra en ${access.priceBranchName}.`
            : 'Lista de precios de venta. Cada cambio queda en el historial.'
        }
        actions={
          access.canPrices && (
            <Button variant="primary" icon={<Percent />} onClick={() => setBulk(true)}>
              Aumento masivo
            </Button>
          )
        }
      />

      {isFranchiseUser ? (
        <FranchisePriceNote />
      ) : (
        !access.canPrices && <Callout>Podés ver los precios. Para cambiarlos hace falta el permiso “Cambiar precios”.</Callout>
      )}

      <Toolbar>
        <SearchBox value={search} onChange={setSearch} placeholder="Buscar producto" />
        <Select value={categoryId} onChange={(e) => setCategoryId(e.target.value)} aria-label="Categoría">
          <option value="">Todas las categorías</option>
          {categories.data?.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </Select>
      </Toolbar>

      <Card>
        <CardBody flush>
          {products.isPending ? (
            <div style={{ padding: '8px 22px 16px', display: 'grid', gap: 12 }}>
              <Skeleton />
              <Skeleton />
              <Skeleton />
            </div>
          ) : (products.data?.length ?? 0) === 0 ? (
            <EmptyState icon={<Tags />} title="No hay productos para mostrar" />
          ) : (
            <div className="bf-table-wrap">
              <table className="bf-table">
                <thead>
                  <tr>
                    <th>Producto</th>
                    <th>Categoría</th>
                    <th>Precio general</th>
                    {showBranch && <th>En {access.priceBranchName}</th>}
                    <th>Actualizado</th>
                    <th aria-label="Acciones" />
                  </tr>
                </thead>
                <tbody>
                  {products.data?.map((p) => {
                    const own = access.priceBranchId ? (p.branchPrices.find((b) => b.branchId === access.priceBranchId)?.priceCents ?? null) : null;
                    return (
                      <tr key={p.id}>
                        <td>
                          <span className="bf-cell-main">{p.name}</span>
                          <div className="bf-cell-sub">{priceUnit(p.unitType)}</div>
                        </td>
                        <td>
                          <span
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 6,
                            }}
                            className="bf-cell-sub"
                          >
                            <span className="bf-thumb" style={{ width: 22, height: 22, borderRadius: 6 }}>
                              <CategoryGlyph icon={p.categoryIcon} />
                            </span>
                            {p.categoryName ?? 'Sin categoría'}
                          </span>
                        </td>
                        <td>
                          {editGeneral ? (
                            <InlinePrice label={`Precio general de ${p.name}`} cents={p.generalPriceCents} onSave={(c) => save(p, null, c)} />
                          ) : (
                            <span className="bf-num">{p.generalPriceCents !== null ? formatMoney(p.generalPriceCents) : '—'}</span>
                          )}
                        </td>
                        {showBranch && (
                          <td>
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 6,
                              }}
                            >
                              {editBranch ? (
                                <InlinePrice
                                  label={`Precio de ${p.name} en ${access.priceBranchName}`}
                                  cents={own}
                                  placeholder={p.generalPriceCents !== null ? centsToPesosInput(p.generalPriceCents) : ''}
                                  onSave={(c) => save(p, access.priceBranchId, c)}
                                />
                              ) : (
                                <span className="bf-num">{formatMoney(own ?? p.generalPriceCents ?? 0)}</span>
                              )}
                              {own !== null ? (
                                editBranch && (
                                  <Button size="sm" variant="ghost" iconOnly icon={<Undo2 />} onClick={() => void reset(p)}>
                                    Volver al precio general
                                  </Button>
                                )
                              ) : (
                                <Badge>General</Badge>
                              )}
                            </div>
                          </td>
                        )}
                        <td className="bf-cell-sub">{formatDate(p.updatedAt)}</td>
                        <td className="bf-cell-actions">
                          <Button size="sm" variant="ghost" iconOnly icon={<History />} onClick={() => setHistory(p)}>
                            Historial de {p.name}
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      {bulk && (
        <BulkPriceDialog
          categories={categories.data ?? []}
          onClose={() => setBulk(false)}
          onApplied={async (n) => {
            toast.success('Precios actualizados', `${n} ${n === 1 ? 'producto' : 'productos'}.`);
            setBulk(false);
            await invalidateInventory(qc);
          }}
        />
      )}
      {history && <PriceHistoryDialog product={history} onClose={() => setHistory(null)} />}
    </>
  );
}

function InlinePrice({
  cents,
  onSave,
  label,
  placeholder,
}: {
  cents: number | null;
  onSave: (cents: number) => Promise<void>;
  label: string;
  placeholder?: string;
}) {
  const initial = centsToPesosInput(cents);
  const [value, setValue] = useState(initial);
  const [saving, setSaving] = useState(false);
  useEffect(() => setValue(initial), [initial]);

  const parsed = pesosToCents(value);
  const dirty = value.trim() !== '' && value !== initial;
  const invalid = dirty && parsed === null;

  async function commit() {
    if (!dirty || parsed === null || saving) return;
    setSaving(true);
    try {
      await onSave(parsed);
    } catch {
      setValue(initial);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="bf-inline-price">
      <input
        className="bf-input"
        inputMode="decimal"
        aria-label={label}
        aria-invalid={invalid || undefined}
        value={value}
        placeholder={placeholder || '$ 0'}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') void commit();
          if (e.key === 'Escape') setValue(initial);
        }}
      />
      {saving ? (
        <Spinner label="Guardando" />
      ) : (
        dirty &&
        !invalid && (
          <Button size="sm" variant="primary" iconOnly icon={<Check />} onClick={() => void commit()}>
            Guardar
          </Button>
        )
      )}
    </div>
  );
}
